<template>
    <div class="w-full px-0 md:px-6">
        <div class="text-center pb-6">
            <h1
                class="text-[28px] md:text-[34px] leading-tight font-semibold text-slate-800 dark:text-slate-100"
            >
                Upgrade to
                <span class="text-[#FD421D]">10xTravel+</span>
            </h1>
            <p
                class="mt-3 text-slate-500 dark:text-slate-400 text-base md:text-lg max-w-[520px] mx-auto"
            >
                Unlock the full power of your rewards with best-in-class
                automated tools and a community of travel experts.
            </p>
        </div>

        <div class="flex justify-center pb-6">
            <button
                type="button"
                class="inline-flex items-center gap-3 select-none rounded"
                @click="toggleBilling"
                aria-label="Toggle billing interval"
            >
                <span
                    class="relative inline-flex w-9 h-5 rounded-full transition-colors"
                    :class="
                        billing === 'year' ? 'bg-[#FD421D]' : 'bg-slate-300 dark:bg-slate-600'
                    "
                >
                    <span
                        class="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white dark:bg-slate-800 shadow dark:shadow-slate-900/50 transform-gpu transition-transform duration-300 ease-in-out"
                        :class="
                            billing === 'year'
                                ? 'translate-x-4'
                                : 'translate-x-0'
                        "
                    ></span>
                </span>
                <span class="text-slate-800 dark:text-slate-200 font-normal text-sm">
                    Pay yearly
                </span>
                <span
                    class="text-xs font-medium text-[#FD421D] bg-orange-50 border border-orange-100 rounded px-2 py-0.5"
                >
                    Save 20%
                </span>
            </button>
        </div>

        <div
            class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-[760px] mx-auto px-4 md:px-0"
        >
            <!-- Free card -->
            <div
                class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 md:p-8 flex flex-col"
            >
                <div class="flex flex-col items-start gap-1 pb-6">
                    <h2 class="text-2xl font-semibold text-slate-800 dark:text-slate-100">Free</h2>
                    <p class="text-slate-500 dark:text-slate-400">$0/month</p>
                </div>
                <button
                    type="button"
                    class="w-full px-4 py-2.5 rounded border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 font-medium hover:border-slate-300 dark:hover:border-slate-600 disabled:opacity-60"
                    :disabled="saving"
                    @click="choose('free')"
                >
                    Continue with free plan
                </button>
                <ul class="mt-6 space-y-3">
                    <li
                        v-for="(item, idx) in freeFeatures"
                        :key="'free-' + idx"
                        class="flex items-start gap-2 text-slate-700 dark:text-slate-300"
                    >
                        <Icon
                            icon="check"
                            :width="22"
                            :height="22"
                            class="text-[#FD421D] mt-0.5 shrink-0"
                        />
                        <span>{{ item }}</span>
                    </li>
                </ul>
            </div>

            <!-- Paid card with solid border + gradient bg -->
            <div
                class="rounded-2xl border border-[#FD421D] bg-gradient-to-b from-orange-50 to-white p-6 md:p-8 flex flex-col"
            >
                <div class="flex flex-col items-start gap-1 pb-6">
                    <h2 class="text-2xl font-semibold text-slate-800">
                        10xTravel+
                    </h2>
                    <p class="text-slate-500">{{ paidPriceLabel }}</p>
                </div>
                <button
                    type="button"
                    class="w-full px-4 py-2.5 rounded bg-[#FD421D] text-white font-medium shadow disabled:opacity-60"
                    :disabled="saving || !paidProduct"
                    @click="choose('paid')"
                >
                    Get 10xTravel+
                </button>
                <p
                    class="mt-6 text-xs font-semibold uppercase tracking-wide text-[#FD421D] mb-3"
                >
                    Everything in Free plus premium upgrades
                </p>
                <ul class="space-y-3">
                    <li
                        v-for="(item, idx) in paidPremiumFeatures"
                        :key="'paid-' + idx"
                        class="flex items-start gap-2 text-slate-700"
                    >
                        <Icon
                            icon="check"
                            :width="22"
                            :height="22"
                            class="text-[#FD421D] mt-0.5 shrink-0"
                        />
                        <span>{{ item }}</span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    products: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const billing = ref('year');

const pickLatestProductByInterval = (interval) => {
    const matches = (props.products || []).filter(
        (p) => p.billing_interval === interval
    );

    if (!matches.length) {
        return null;
    }

    return [...matches].sort((a, b) => {
        const aId = Number(a?.id) || 0;
        const bId = Number(b?.id) || 0;
        return bId - aId;
    })[0];
};

const monthlyProduct = computed(() => pickLatestProductByInterval('month'));
const yearlyProduct = computed(() => pickLatestProductByInterval('year'));

const paidProduct = computed(() =>
    billing.value === 'year' ? yearlyProduct.value : monthlyProduct.value
);

const paidPriceLabel = computed(() => {
    if (billing.value === 'year' && yearlyProduct.value) {
        const annual = parseFloat(yearlyProduct.value.price);
        if (!isNaN(annual)) {
            const monthly = annual / 12;
            return '$' + monthly.toFixed(2) + '/month';
        }
    }
    if (billing.value === 'month' && monthlyProduct.value) {
        const monthly = parseFloat(monthlyProduct.value.price);
        if (!isNaN(monthly)) {
            return '$' + monthly.toFixed(monthly % 1 === 0 ? 0 : 2) + '/month';
        }
    }
    return billing.value === 'year' ? '$8.33/month' : '$10/month';
});

const freeFeatures = [
    'My Wallet',
    'My Points',
    'My Next Card',
    'My Credit Score',
    '10xTravel Course',
    'Lounge Finder',
    'Redemption Tracker',
    'Companion Pass Tracker',
    'Track benefits for one card',
];

const freeToolsOnly = freeFeatures.filter(
    (f) => f !== 'Track benefits for one card'
);

const paidPremiumFeatures = [
    'Track benefits for every one of your cards',
    'Best card to swipe for every category',
    'Automated redemption import in the Redemption Tracker',
    'Real-time award alerts',
    'Members-only travel community',
];

function toggleBilling() {
    billing.value = billing.value === 'year' ? 'month' : 'year';
}

function choose(plan) {
    if (props.saving) return;
    emit('select', { plan, billing_interval: billing.value });
}
</script>
